import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, GripVertical, Save, RotateCcw, HelpCircle, X, Trash2 } from 'lucide-react';

const AdminCollectionsOrder = () => {
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentEditIndex, setCurrentEditIndex] = useState(null);
  const [tempHelpText, setTempHelpText] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/collections/management`);
      const data = await response.json();
      setCollections(data.collections);
      setLoading(false);
    } catch (error) {
      alert('Erreur lors du chargement des collections: ' + error.message);
      setLoading(false);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    
    if (draggedItem === null || draggedItem === index) return;

    const newCollections = [...collections];
    const draggedCollection = newCollections[draggedItem];
    
    // Remove from old position
    newCollections.splice(draggedItem, 1);
    
    // Insert at new position
    newCollections.splice(index, 0, draggedCollection);
    
    // Update display_order
    newCollections.forEach((col, idx) => {
      col.display_order = idx + 1;
    });
    
    setCollections(newCollections);
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const moveUp = (index) => {
    if (index === 0) return;
    
    const newCollections = [...collections];
    [newCollections[index - 1], newCollections[index]] = [newCollections[index], newCollections[index - 1]];
    
    // Update display_order
    newCollections.forEach((col, idx) => {
      col.display_order = idx + 1;
    });
    
    setCollections(newCollections);
  };

  const moveDown = (index) => {
    if (index === collections.length - 1) return;
    
    const newCollections = [...collections];
    [newCollections[index], newCollections[index + 1]] = [newCollections[index + 1], newCollections[index]];
    
    // Update display_order
    newCollections.forEach((col, idx) => {
      col.display_order = idx + 1;
    });
    
    setCollections(newCollections);
  };

  const resetOrder = async () => {
    if (!window.confirm('Réinitialiser l\'ordre alphabétique ?')) return;
    
    const sorted = [...collections].sort((a, b) => a.name.localeCompare(b.name));
    sorted.forEach((col, idx) => {
      col.display_order = idx + 1;
    });
    
    setCollections(sorted);
  };

  const saveOrder = async () => {
    setSaving(true);
    
    try {
      const orderData = collections.map((col, index) => ({
        name: col.name,
        display_order: index + 1,
        help_text: col.help_text || null
      }));

      const response = await fetch(`${BACKEND_URL}/api/collections/update-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        alert('✅ Ordre des collections sauvegardé !');
        fetchCollections(); // Recharger pour confirmer
      } else {
        alert('Erreur: ' + data.message);
      }
    } catch (error) {
      alert('Erreur lors de la sauvegarde: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const openHelpModal = (index) => {
    setCurrentEditIndex(index);
    setTempHelpText(collections[index].help_text || '');
    setModalOpen(true);
  };

  const closeHelpModal = () => {
    setModalOpen(false);
    setCurrentEditIndex(null);
    setTempHelpText('');
  };

  const saveHelpText = async () => {
    if (currentEditIndex === null) return;

    // Update locally
    const newCollections = [...collections];
    newCollections[currentEditIndex].help_text = tempHelpText;
    setCollections(newCollections);

    // Save to backend immediately
    setSaving(true);
    try {
      const orderData = newCollections.map((col, index) => ({
        name: col.name,
        display_order: index + 1,
        help_text: col.help_text || null
      }));

      const response = await fetch(`${BACKEND_URL}/api/collections/update-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        alert('✅ Texte d\'aide sauvegardé !');
        closeHelpModal();
        fetchCollections(); // Recharger pour confirmer
      } else {
        alert('Erreur: ' + data.message);
      }
    } catch (error) {
      alert('Erreur lors de la sauvegarde: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteCollection = async (index) => {
    const collection = collections[index];
    
    if (!window.confirm(
      `⚠️ ATTENTION\n\n` +
      `Voulez-vous vraiment supprimer la collection "${collection.name}" ?\n\n` +
      `Cette action va :\n` +
      `✓ Supprimer l'ordre et le texte d'aide de cette collection\n` +
      `✓ Les produits de cette collection resteront dans la base\n\n` +
      `Vous pourrez refaire un import Excel pour mettre à jour le nom.`
    )) {
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/collections/${encodeURIComponent(collection.name)}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      
      if (data.status === 'success' || data.status === 'warning') {
        alert('✅ Collection supprimée de la liste !');
        fetchCollections(); // Recharger la liste
      } else {
        alert('Erreur: ' + data.message);
      }
    } catch (error) {
      alert('Erreur lors de la suppression: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <p className="text-xl">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-200 rounded"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-3xl font-bold">Ordre des Collections</h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={resetOrder}
              className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              <RotateCcw size={16} />
              Ordre Alphabétique
            </button>
            <button
              onClick={saveOrder}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300"
            >
              <Save size={16} />
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">💡 Instructions</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Glissez-déposez les collections pour changer leur ordre</li>
            <li>• Utilisez les flèches ↑ ↓ pour déplacer une collection</li>
            <li>• Cliquez sur l'icône <HelpCircle className="inline" size={14} /> pour modifier le texte d'aide</li>
            <li>• Cliquez sur l'icône <Trash2 className="inline text-red-500" size={14} /> pour supprimer une collection de cette liste</li>
            <li>• L'ordre défini ici sera reflété dans la sidebar du site</li>
            <li>• N'oubliez pas de cliquer sur "Sauvegarder" après avoir réordonné !</li>
          </ul>
        </div>

        {/* Liste des collections */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b bg-gray-50">
            <h2 className="font-semibold">
              {collections.length} collection{collections.length > 1 ? 's' : ''}
            </h2>
          </div>

          <div className="divide-y">
            {collections.map((collection, index) => (
              <div
                key={collection.name}
                className="p-4 hover:bg-gray-50 transition-colors"
              >
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center justify-between cursor-move ${
                    draggedItem === index ? 'bg-blue-50 opacity-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <GripVertical size={20} className="text-gray-400" />
                    <span className="font-medium text-gray-500 w-8">#{index + 1}</span>
                    <span className="font-semibold text-gray-900">{collection.name}</span>
                    {!collection.is_ordered && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                        Nouvelle
                      </span>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => openHelpModal(index)}
                      className={`p-2 hover:bg-gray-100 rounded ${collection.help_text ? 'text-blue-500' : 'text-gray-400'}`}
                      title="Modifier le texte d'aide"
                    >
                      <HelpCircle size={18} />
                    </button>
                    <button
                      onClick={() => deleteCollection(index)}
                      className="p-2 hover:bg-red-100 rounded text-red-500"
                      title="Supprimer cette collection"
                    >
                      <Trash2 size={18} />
                    </button>
                    <button
                      onClick={() => moveUp(index)}
                      disabled={index === 0}
                      className="p-2 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Monter"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveDown(index)}
                      disabled={index === collections.length - 1}
                      className="p-2 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Descendre"
                    >
                      ↓
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer info */}
        <div className="mt-6 text-center text-sm text-gray-600">
          <p>Les collections seront affichées dans cet ordre sur le site public.</p>
        </div>
      </div>

      {/* Modal for Help Text Editing */}
      {modalOpen && currentEditIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                Texte d'aide : {collections[currentEditIndex].name}
              </h3>
              <button
                onClick={closeHelpModal}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Texte d'aide contextuelle
              </label>
              <textarea
                value={tempHelpText}
                onChange={(e) => setTempHelpText(e.target.value)}
                placeholder="Entrez quelques phrases d'aide pour cette collection..."
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="6"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-2">
                Ce texte apparaîtra dans un tooltip au survol de l'icône d'aide sur le site public.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
              <button
                onClick={closeHelpModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                disabled={saving}
              >
                Annuler
              </button>
              <button
                onClick={saveHelpText}
                disabled={saving}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300"
              >
                {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCollectionsOrder;
